import type { AppConfig } from 'vue'
import * as Sentry from '@sentry/vue'
import {
  extraErrorDataIntegration,
  rewriteFramesIntegration,
  reportingObserverIntegration,
  httpClientIntegration,
} from '@sentry/integrations'

import { IGNORE_ERRORS, INJECTED_SCRIPT_FUNCTIONS, INJECTED_SCRIPT_SOURCE_PREFIX } from '@layers/web/constants/sentry.ts'

// A page's validate() and createError({ status: 404 }) answer unknown URLs with the 404 page,
// which is the right outcome and not an error
const isNotFoundError = (exception: unknown): boolean => (
  typeof exception === 'object' &&
  exception !== null &&
  'statusCode' in exception &&
  exception.statusCode === 404
)

// Errors thrown by a script the host app or a bot injected into the page: Meta's bridge functions in the
// stack, or WebKit's "user-script:" source kept on the error by extraErrorDataIntegration
const isInjectedScriptError = (event: Sentry.Event): boolean => (
  (event.exception?.values ?? []).some(({ type, stacktrace }) => (
    String(event.contexts?.[type ?? '']?.sourceURL ?? '').startsWith(INJECTED_SCRIPT_SOURCE_PREFIX) ||
    (stacktrace?.frames ?? []).some(frame => INJECTED_SCRIPT_FUNCTIONS.some(name => frame.function?.includes(name)))
  ))
)

/**
 * @see https://docs.sentry.io/platforms/javascript/guides/vue/apis
 */
export default defineNuxtPlugin((nuxtApp) => {
  const { SENTRY, VERCEL_DEPLOYMENT_ID } = nuxtApp.$config.public

  const userStore = useUserStore()
  const {
    isLoggedIn,
    user,
    getToken,
  } = storeToRefs(userStore)

  if (
    !SENTRY.dsn ||
    SENTRY.envName === 'development'
  ) {
    return {
      provide: {
        sentrySetContext:
          (...params: Array<any>) => console.info('[Sentry Client] Set context: ', ...params),
        sentrySetUser:
          (...params: Array<any>) => console.info('[Sentry Client] Set user: ', ...params),
        sentrySetTag:
          (...params: Array<any>) => console.info('[Sentry Client] Set tag: ', ...params),
        sentryAddBreadcrumb:
          (...params: Array<any>) => console.info('[Sentry Client] Add Breadcrumb: ', ...params),
        sentryCaptureException:
          (...params: Array<any>) => console.error('[Sentry Client] Captured exception: ', ...params),
        sentryCaptureMessage:
          (...params: Array<any>) => console.error('[Sentry Client] Captured message: ', ...params),
      },
    }
  }

  const { vueApp } = nuxtApp
  const router = useRouter()

  // Nuxt installs handleVueError, which turns every Vue error into the error page; this plugin
  // has always replaced it, so clear it before Sentry installs its own handler.
  vueApp.config.errorHandler = undefined

  // @sentry/vue 7 names components from options.name, and production builds of <script setup>
  // components only carry __name, so its componentName is <Anonymous>. The component is
  // remembered per error in the wrapper below and tagged in beforeSend instead.
  const vueComponentByError = new WeakMap<Error, string>()

  Sentry.init({
    app: [vueApp],
    logErrors: false,
    dsn: SENTRY.dsn,
    environment: SENTRY.environment,
    enabled: true,
    release: SENTRY.release,
    ignoreErrors: IGNORE_ERRORS,
    // Drop errors thrown inside third-party scripts: the Crisp chat widget, customer.io,
    // Mynewsdesk's newsroom script, whose hashchange listener outlives /info/pressrum, and the
    // scripts Meta's in-app browsers load from iabjs:// URLs
    denyUrls: [
      /^https?:\/\/client\.crisp\.chat\//,
      /^https?:\/\/cdp-eu\.customer\.io\/v1\//,
      /\/hosted_newsroom\.js/,
      /^iabjs:\/\//,
    ],
    normalizeDepth: 7,
    normalizeMaxBreadth: 400,
    sendDefaultPii: true,
    integrations: [
      Sentry.browserTracingIntegration({ router }),
      Sentry.dedupeIntegration(),
      extraErrorDataIntegration(),
      rewriteFramesIntegration(),
      reportingObserverIntegration(),
      httpClientIntegration(),
    ],
    beforeSend (event, hint) {
      // Don't send errors related to chunk errors
      if (
        nuxtApp.$config.public.skewed ||
        nuxtApp.$config.public.chunkError
      ) {
        return null
      }

      // Expected 404s stay off Sentry, as the Nitro error hook already keeps them off on the server
      if (isNotFoundError(hint.originalException)) {
        return null
      }

      if (isInjectedScriptError(event)) {
        return null
      }

      if (hint.originalException instanceof Error && vueComponentByError.has(hint.originalException)) {
        event.tags = { ...event.tags, 'vue.component': vueComponentByError.get(hint.originalException) }
      }

      return event
    },
    tracesSampleRate: 0.1,
    tracePropagationTargets: [
      /^localhost(:\d+)?$/,
      /^https:\/\/(www\.|dev\.)?rolfsbuss\.se$/,
      /^https:\/\/(www\.|dev\.)?solresor\.se$/,
      /^https:\/\/(www\.|dev\.)?rolfsreiser\.no$/,
      /^https:\/\/(www\.|dev\.)?rolfsrejser\.da$/,
    ],
  })

  // Wrap the handler Sentry.init installed: the component is recorded before Sentry's deferred
  // capture runs, without relying on how Sentry chains app.config.errorHandler internally. Read
  // through a function, since the assignment above narrows the property to undefined for the type checker.
  const installedVueErrorHandler = (): AppConfig['errorHandler'] => vueApp.config.errorHandler
  const sentryErrorHandler = installedVueErrorHandler()
  vueApp.config.errorHandler = (error, instance, info) => {
    if (error instanceof Error) {
      vueComponentByError.set(error, instance?.$.type.name || instance?.$.type.__name || 'Anonymous')
    }

    sentryErrorHandler?.call(vueApp, error, instance, info)
  }

  watch(isLoggedIn, (value) => {
    if (value) {
      Sentry.setUser({
        email: user.value?.email,
        jwt: getToken.value,
      })
    }
  }, { immediate: true })

  Sentry.setContext('cookies', { value: document.cookie })
  Sentry.setContext('deploymentId', { value: VERCEL_DEPLOYMENT_ID })

  return {
    provide: {
      sentrySetContext: Sentry.setContext,
      sentrySetUser: Sentry.setUser,
      sentrySetTag: Sentry.setTag,
      sentryAddBreadcrumb: Sentry.addBreadcrumb,
      sentryCaptureException: Sentry.captureException,
      sentryCaptureMessage: Sentry.captureMessage,
    },
  }
})
